/**
 * Form drafts in `localStorage`, keyed by route — docs/08 §3.5.
 *
 * The person form is long: identity, two fuzzy dates, a place, a biography, and
 * a list of connections each with their own dates and bases. Someone entering a
 * family works through dozens of these in a sitting, and the ways to lose one
 * are mundane — a mistyped URL, a link opened in the same tab to check a
 * duplicate, a laptop lid. Losing that work is not a crash, so nothing reports
 * it; the person simply retypes it, and trusts the form a little less.
 *
 * The draft is per-browser and never leaves it: this is a convenience, not
 * storage. Anything that must survive belongs in the database, behind a vote.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PREFIX = 'famtree.draft.';

export interface Draft<T> {
  /** The restored value, if there was one — apply it instead of the defaults. */
  restored: T | null;
  save: (value: T) => void;
  discard: () => void;
}

export function useDraft<T>(key: string, enabled = true): Draft<T> {
  const storageKey = `${PREFIX}${key}`;
  // Read once, on mount: a draft that changed in another tab mid-edit would be a
  // surprise, not a help.
  const [restored] = useState<T | null>(() => {
    if (!enabled) return null;
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      // A private window, a full quota, a draft written by an older version of
      // the form — none of which is worth an error in front of the user.
      return null;
    }
  });

  const timer = useRef<number | null>(null);

  const save = useCallback(
    (value: T) => {
      if (!enabled) return;
      if (timer.current !== null) window.clearTimeout(timer.current);
      // Debounced: this fires on every keystroke of a long form.
      timer.current = window.setTimeout(() => {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(value));
        } catch {
          /* Out of quota or storage denied — the form still works. */
        }
      }, 500);
    },
    [enabled, storageKey],
  );

  const discard = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      /* Nothing to do: the draft is a convenience, its removal likewise. */
    }
  }, [storageKey]);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  // A stable object: callers put this in effect dependency lists, and a fresh
  // literal every render would re-run them every render.
  return useMemo(() => ({ restored, save, discard }), [restored, save, discard]);
}
